import { z } from 'zod';
import oktonBankApi from './oktonBankApi';
import type { ContaBancaria } from './hooks/useContasBancarias';
import type {
  FiltrosDashboard,
  DadosDashboard,
  DadosEvolucao,
  ContaDashboard,
  DadosPendencias,
  Movimentacao,
  UltimaConciliacao,
} from './hooks/useDashboard';

export const RECURSOS_PAINEL = [
  'indicadores',
  'evolucao',
  'contas',
  'pendencias',
  'movimentacoes',
  'ultimasConciliacoes',
  'contasCadastro',
] as const;
export type RecursoPainel = typeof RECURSOS_PAINEL[number];
export interface DadosPainel {
  indicadores: DadosDashboard;
  evolucao: DadosEvolucao;
  contas: { rows: ContaDashboard[] };
  pendencias: DadosPendencias;
  movimentacoes: { rows: Movimentacao[] };
  ultimasConciliacoes: { rows: UltimaConciliacao[] };
  contasCadastro: { rows: ContaBancaria[] };
}
export type ResultadoPainel<T> =
  | { status: number; data: T; error?: never }
  | { status: number; error: { code: string; message: string }; data?: never };
export type RespostaPainel = {
  resources: { [K in RecursoPainel]?: ResultadoPainel<DadosPainel[K]> };
};

const texto = z.string();
const opcional = texto.nullable();
const numero = z.number().finite();
const ponto = z.object({
  conciliados: numero,
  pendentes: numero,
  divergentes: numero,
  entradas: texto,
  saidas: texto,
  saldo: texto,
});
const resumo = z.object({ quantidade: numero, valor: texto, conta: opcional });
const schemas: Record<RecursoPainel, z.ZodTypeAny> = {
  indicadores: z.object({
    saldototal: texto,
    saldomesanterior: texto,
    variacaosaldo: opcional,
    movimentoperiodo: texto,
    contas: numero,
    extratototal: numero,
    extratopendente: numero,
    valorpendente: texto,
    extratoconciliado: numero,
    divergencias: numero,
    valordivergente: texto,
    conciliados: numero,
    titulopendente: numero,
    valorconciliado: texto,
  }),
  evolucao: z.object({
    agrupamento: z.enum(['dia', 'semana', 'mes']),
    saldoinicial: texto,
    rows: z.array(ponto.extend({ periodo: texto })),
    lancamentos: z
      .array(
        z.object({
          id_exb: texto,
          descricao: texto,
          conta: opcional,
          entrada: texto,
          saida: texto,
          conciliado: texto,
        }),
      )
      .nullable(),
    horas: z.array(ponto.extend({ hora: texto })).nullable(),
    temhora: z.boolean(),
  }),
  contas: z.object({
    rows: z.array(
      z.object({
        id_cts: texto,
        descricao_cts: texto,
        numero_cts: opcional,
        nome_ban: opcional,
        confcomp_ban: opcional,
        saldo: texto,
        total: numero,
        pendentes: numero,
        valorpendente: texto,
        conciliados: numero,
        divergencias: numero,
        valordivergente: texto,
        percentual: numero.nullable(),
        ultimaatualizacao: opcional,
        sematualizacao: z.boolean(),
      }),
    ),
  }),
  pendencias: z.object({
    semcorrespondencia: resumo,
    possiveiscorrespondencias: resumo,
    divergencias: resumo,
    contassematualizacao: z.object({
      quantidade: numero,
      contas: z.array(
        z.object({
          id_cts: texto,
          descricao_cts: texto,
          ultimaatualizacao: opcional,
        }),
      ),
    }),
  }),
  movimentacoes: z.object({
    rows: z.array(
      z.object({ categoria: texto, quantidade: numero, valor: texto }),
    ),
  }),
  ultimasConciliacoes: z.object({
    rows: z.array(
      z.object({
        id_cbc: texto,
        cadastro_cbc: texto,
        data_exb: texto,
        conta_exb: texto,
        descricao_cts: opcional,
        descricao_exb: texto,
        valor_cbc: texto,
        valor_exb: texto,
        tipo_cbc: texto,
        origem_cbc: texto,
        usuario: opcional,
        divergente: z.boolean(),
      }),
    ),
  }),
  contasCadastro: z.object({
    rows: z.array(
      z.object({
        id_cts: texto,
        filial_cts: texto,
        descricao_cts: texto,
        numero_cts: texto,
        digito_cts: texto,
        tipo_cts: texto,
        agencia_cts: opcional,
        moeda_ctm: opcional,
        saldoinicial_cts: texto,
        id_ban: opcional,
        nome_ban: opcional,
        confcomp_ban: opcional,
        agencia_agc: opcional,
        digito_agc: opcional,
        posto_agc: opcional,
        nome_moe: opcional,
      }),
    ),
  }),
};

const envelope = z.union([
  z
    .object({ status: z.number().int().min(200).max(299), data: z.unknown() })
    .strict(),
  z
    .object({
      status: z
        .number()
        .int()
        .min(400)
        .max(599)
        .refine(status => status !== 401),
      error: z
        .object({ code: z.string().min(1), message: z.string().min(1) })
        .strict(),
    })
    .strict(),
]);
const resposta = z.object({ resources: z.record(envelope) }).strict();
const INVALIDA = 'O Bancário recebeu uma resposta inválida. Tente novamente.';

/** Uses the P2 Axios instance; authentication belongs to its shared adapter. */
export async function consultarDashboardPainel(
  filtros: FiltrosDashboard,
  recursos?: RecursoPainel[],
): Promise<RespostaPainel> {
  const ids = recursos ?? [...RECURSOS_PAINEL];
  if (
    !ids.length ||
    new Set(ids).size !== ids.length ||
    ids.some(id => !RECURSOS_PAINEL.includes(id))
  )
    throw new Error(INVALIDA);
  const { data } = await oktonBankApi.get<unknown>('/dashboard/painel', {
    params: { ...filtros, ...(recursos ? { recursos } : {}) },
  });
  const validada = resposta.safeParse(data);
  if (!validada.success) throw new Error(INVALIDA);
  const entries = validada.data.resources;
  if (
    Object.keys(entries).length !== ids.length ||
    ids.some(id => !Object.prototype.hasOwnProperty.call(entries, id))
  )
    throw new Error(INVALIDA);
  // Validate the entire requested group before exposing any data. Keep the
  // original payload, including monetary strings and compatible extra fields.
  ids.forEach(id => {
    const item = entries[id];
    if (
      item.status < 300 &&
      (!('data' in item) || !schemas[id].safeParse(item.data).success)
    )
      throw new Error(INVALIDA);
  });
  return data as RespostaPainel;
}
